<template>
  <div>
    <div v-if="!d" class="text-center py-3">
      <b-spinner v-if="!notFound" />
      <div v-else>
        <h5>Filo bulunamadı</h5>
        <b-button variant="outline-primary" :to="{ name: 'Fleet' }">Listeye dön</b-button>
      </div>
    </div>

    <template v-else>
      <!-- Üst bölüm -->
      <b-card class="mb-1">
        <div class="d-flex flex-wrap justify-content-between align-items-center" style="gap: 0.75rem;">
          <div>
            <h3 class="mb-25">{{ f.name }}</h3>
            <b-badge :variant="f.mode === 'LIVE' ? 'danger' : 'light-info'" class="mr-50">{{ f.mode === 'LIVE' ? 'LIVE (gerçek)' : 'TESTNET (kağıt)' }}</b-badge>
            <b-badge :variant="status.variant" class="mr-50">{{ status.label }}</b-badge>
            <b-badge :variant="d.check.tested ? 'light-success' : 'light-warning'" class="mr-50">{{ d.check.tested ? 'Test edilen ayar' : 'Test edilmemiş ayar' }}</b-badge>
            <b-badge :variant="pool.variant">Havuz: {{ pool.label }}</b-badge>
            <div><small class="text-muted">{{ f.slotCount }} slot · {{ f.leverage }}x · {{ sizing.label }} · bütçe {{ usd(f.budget) }}<span v-if="f.startedAt"> · son başlatma {{ time(f.startedAt) }}</span></small></div>
          </div>
          <div class="d-flex flex-wrap" style="gap: 0.5rem;">
            <b-button v-if="canStart" variant="success" :disabled="busy" @click="setStatus('start')">Başlat</b-button>
            <b-button v-if="f.status === 'RUNNING'" variant="warning" :disabled="busy" @click="setStatus('pause')">Duraklat</b-button>
            <b-button v-if="['RUNNING', 'PAUSED', 'DRAFT'].includes(f.status)" variant="outline-secondary" :disabled="busy" @click="setStatus('stop')">Durdur</b-button>
            <b-button v-if="['RUNNING', 'PAUSED'].includes(f.status)" variant="danger" :disabled="busy" @click="onKill">ACİL KAPAT</b-button>
            <b-button v-if="['DRAFT', 'STOPPED'].includes(f.status)" variant="outline-danger" :disabled="busy" @click="onDelete">Sil</b-button>
            <b-button variant="outline-secondary" :to="{ name: 'Fleet' }">Liste</b-button>
          </div>
        </div>
      </b-card>

      <b-alert :show="!d.runnerEnabled" variant="warning" class="mb-1">
        <div class="p-1"><small>Filo motoru sunucuda <strong>kapalı</strong> (FLEET_RUNNER_ENABLED): başlatılsa da işlem yapmaz, acil kapatma da uygulanmaz.</small></div>
      </b-alert>
      <b-alert :show="f.status === 'KILLED'" variant="danger" class="mb-1">
        <div class="p-1"><small>Acil kapatma sürüyor: açık pozisyonlar piyasa fiyatından kapatılıyor. Hepsi kapanınca durum <strong>Durduruldu</strong> olur.</small></div>
      </b-alert>
      <b-alert :show="f.status === 'PAUSED'" variant="warning" class="mb-1">
        <div class="p-1"><small>Duraklatıldı: yeni pozisyon açılmaz, açık pozisyonlar kendi stop / kâr al / süre çıkışlarıyla kapanmaya devam eder.</small></div>
      </b-alert>
      <b-alert :show="f.status === 'DRAFT'" variant="secondary" class="mb-1">
        <div class="p-1"><small>Taslak: ayarları kontrol edip <strong>Başlat</strong> de. İlk işlem başlatmadan sonraki ilk mum kapanışında yapılır.</small></div>
      </b-alert>
      <b-alert :show="k.walletDepleted" variant="danger" class="mb-1">
        <div class="p-1"><small>Cüzdan tükendi: yeni pozisyon açılmıyor.</small></div>
      </b-alert>

      <!-- Göstergeler -->
      <b-row class="text-center">
        <b-col v-for="t in tiles" :key="t.label" cols="6" md="4" xl="2" class="mb-1">
          <div class="border rounded p-1 h-100">
            <div class="text-muted"><small>{{ t.label }}</small></div>
            <h4 class="mb-0" :class="t.cls">{{ t.value }}</h4>
            <small v-if="t.sub" class="text-muted">{{ t.sub }}</small>
          </div>
        </b-col>
      </b-row>

      <!-- Beklenen / gerçekleşen -->
      <b-card title="Aylık gerçekleşen K/Z" class="mb-1">
        <small class="text-muted d-block mb-50">
          Yalnız kapanan pozisyonlar. Çizgiler test referansıdır (yalnız test edilen ayarda ve Sabit modda); geçmiş garanti değildir.
          Maks DD burada kapanan işlemlerden ölçülür, açık pozisyonların anlık düşüşü dahil değildir.
        </small>
        <div v-if="!d.monthly.length" class="text-muted py-2 text-center">Henüz kapanan pozisyon yok.</div>
        <vue-apex-charts v-else type="bar" height="240" :options="chartOptions" :series="chartSeries" />
      </b-card>

      <b-card no-body>
        <b-tabs card lazy @input="onTab">
          <!-- Motorlar -->
          <b-tab title="Motorlar">
            <b-table :items="d.engines" :fields="engineFields" responsive small class="mb-0">
              <template #cell(enabled)="row">
                <b-form-checkbox :checked="row.item.enabled" switch :disabled="busy" @change="toggleEngine(row.item.engine, $event)" />
              </template>
              <template #cell(label)="row">
                <strong>{{ row.item.label }}</strong>
                <div><small class="text-muted">{{ engineDesc(row.item.engine) }}</small></div>
              </template>
              <template #cell(open)="row">{{ row.item.open }} / {{ row.item.max == null ? '∞' : row.item.max }}<small v-if="row.item.min" class="text-muted"> (ayrılmış {{ row.item.min }})</small></template>
              <template #cell(winRate)="row">{{ row.item.trades ? `${row.item.winRate.toFixed(1)}%` : '—' }}</template>
              <template #cell(net)="row"><span :class="pnlClass(row.item.net)">{{ usd(row.item.net, true) }}</span></template>
            </b-table>
            <small class="text-muted">Motor anahtarı yalnız yeni sinyalleri etkiler; açık pozisyonlar kendi çıkışlarıyla devam eder.</small>
          </b-tab>

          <!-- Pozisyonlar -->
          <b-tab title="Pozisyonlar">
            <b-form-radio-group v-model="posStatus" buttons size="sm" button-variant="outline-primary" class="mb-1" :options="[{ text: 'Açık', value: 'OPEN' }, { text: 'Kapanan', value: 'CLOSED' }, { text: 'Tümü', value: '' }]" @change="loadPositions" />
            <b-table :items="positions.rows" :fields="positionFields" responsive small show-empty empty-text="Pozisyon yok." class="mb-0">
              <template #cell(engineLabel)="row"><b-badge variant="light-primary">{{ row.item.engineLabel }}</b-badge></template>
              <template #cell(side)="row"><b-badge :variant="row.item.side === 'LONG' ? 'light-success' : 'light-danger'">{{ row.item.side }}</b-badge></template>
              <template #cell(entryPrice)="row">{{ num(row.item.entryPrice, 6) }}<div><small class="text-muted">{{ row.item.leverage }}x · marj {{ usd(row.item.marginUsd) }}</small></div></template>
              <template #cell(exit)="row">
                <span v-if="row.item.status === 'CLOSED'">{{ num(row.item.exitPrice, 6) }}</span>
                <span v-else><small class="text-muted">stop</small> {{ num(row.item.stop, 6) }}<span v-if="row.item.tp != null"><br><small class="text-muted">kâr al</small> {{ num(row.item.tp, 6) }}</span></span>
              </template>
              <template #cell(pnl)="row">
                <span v-if="row.item.status === 'CLOSED'" :class="pnlClass(row.item.pnl)">{{ usd(row.item.pnl, true) }}<br><small>{{ pct(row.item.pnlPercent) }}</small></span>
                <span v-else-if="row.item.unrealizedPnl != null" :class="pnlClass(row.item.unrealizedPnl)">{{ usd(row.item.unrealizedPnl, true) }}<br><small class="text-muted">anlık</small></span>
                <small v-else class="text-muted">fiyat bekleniyor</small>
              </template>
              <template #cell(openedAt)="row"><small>{{ time(row.item.openedAt) }}</small></template>
              <template #cell(end)="row">
                <small v-if="row.item.status === 'CLOSED'">{{ time(row.item.closedAt) }}<br>{{ closeReason(row.item.closeReason) }}</small>
                <small v-else-if="row.item.timeExitAt">süre çıkışına {{ duration(row.item.timeExitAt - now) }}</small>
                <small v-else class="text-muted">süre sınırı yok</small>
              </template>
            </b-table>
            <small v-if="positions.total > positions.rows.length" class="text-muted">Son {{ positions.rows.length }} / {{ positions.total }} gösteriliyor.</small>
          </b-tab>

          <!-- Sinyaller -->
          <b-tab title="Sinyaller">
            <div class="d-flex flex-wrap mb-1" style="gap: 0.5rem;">
              <b-button size="sm" :variant="!decFilter.outcome ? 'primary' : 'outline-primary'" @click="setDecFilter({})">Tümü ({{ decTotal }})</b-button>
              <b-button size="sm" :variant="decFilter.outcome === 'OPENED' ? 'success' : 'outline-success'" @click="setDecFilter({ outcome: 'OPENED' })">Açıldı ({{ openedCount }})</b-button>
              <b-button
                v-for="c in skipCounts"
                :key="c.reason || 'none'"
                size="sm"
                :variant="decFilter.reason === c.reason ? 'warning' : 'outline-warning'"
                @click="setDecFilter({ outcome: 'SKIPPED', reason: c.reason })"
              >
                {{ reasonLabel(c.reason) }} ({{ c.count }})
              </b-button>
            </div>
            <b-table :items="decisions.rows" :fields="decisionFields" responsive small show-empty empty-text="Henüz sinyal yok." class="mb-0">
              <template #cell(decisionMs)="row"><small>{{ time(row.item.decisionMs) }}</small></template>
              <template #cell(engineLabel)="row"><b-badge variant="light-primary">{{ row.item.engineLabel }}</b-badge></template>
              <template #cell(side)="row"><b-badge :variant="row.item.side === 'LONG' ? 'light-success' : 'light-danger'">{{ row.item.side }}</b-badge></template>
              <template #cell(outcome)="row">
                <b-badge :variant="row.item.outcome === 'OPENED' ? 'light-success' : 'light-warning'">{{ row.item.outcome === 'OPENED' ? 'Açıldı' : 'Atlandı' }}</b-badge>
                <div v-if="row.item.reason"><small>{{ reasonLabel(row.item.reason) }}</small></div>
              </template>
              <template #cell(refPrice)="row">{{ num(row.item.refPrice, 6) }}</template>
              <template #cell(sizeMult)="row">{{ num(row.item.sizeMult, 2) }}</template>
            </b-table>
            <div class="text-center mt-1">
              <b-button v-if="decisions.rows.length >= decLimit" size="sm" variant="outline-secondary" @click="decLimit += 100; loadDecisions()">Daha fazla</b-button>
            </div>
          </b-tab>

          <!-- Ayarlar -->
          <b-tab title="Ayarlar">
            <b-row v-if="edit">
              <b-col lg="8">
                <risk-form v-model="edit" :locked="locks" :api-keys="apiKeys" :live-enabled="d.liveEnabled" />
                <hr>
                <engine-slots-editor v-model="edit" :tested="testedEngines" :slots-locked="locks.slots || ''" @reset-engines="resetEngines" />
                <div class="d-flex mt-2" style="gap: 0.5rem;">
                  <b-button variant="primary" :disabled="busy || !dirty || hasErrors" @click="onSave">Kaydet</b-button>
                  <b-button variant="outline-secondary" :disabled="!dirty" @click="resetEdit">Değişiklikleri geri al</b-button>
                </div>
              </b-col>
              <b-col lg="4">
                <check-panel :check="editCheck" :expected="editExpected" />
              </b-col>
            </b-row>
          </b-tab>

          <!-- Botlar -->
          <b-tab title="Botlar">
            <b-table :items="d.bots" :fields="botFields" responsive small class="mb-0">
              <template #cell(name)="row">{{ row.item.name }} <b-badge variant="light-secondary" class="ml-50">Filo tarafından yönetiliyor</b-badge></template>
              <template #cell(symbol)="row">{{ row.item.symbol || '—' }}</template>
              <template #cell(capitalCurrent)="row">{{ usd(row.item.capitalCurrent) }}</template>
              <template #cell(realizedPnl)="row"><span :class="pnlClass(row.item.realizedPnl)">{{ usd(row.item.realizedPnl, true) }}</span></template>
            </b-table>
            <small class="text-muted">Slot botları yalnız filo tarafından yönetilir; bot sayfasından elle başlatılamaz. Slot başı sermaye bütçe / slot sayısıdır.</small>
            <div class="mt-50"><b-button size="sm" variant="outline-primary" :to="{ name: 'Bots' }">Bot sayfasına git</b-button></div>
          </b-tab>
        </b-tabs>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BBadge, BButton, BAlert, BSpinner, BTabs, BTab, BTable, BFormCheckbox, BFormRadioGroup,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import axiosIns from '@/libs/axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import RiskForm from './components/RiskForm.vue'
import EngineSlotsEditor from './components/EngineSlotsEditor.vue'
import CheckPanel from './components/CheckPanel.vue'
import {
  ENGINE_INFO, STATUS_INFO, SIZING_INFO, POOL_INFO, REASON_INFO, CLOSE_REASON_INFO,
  formatUsd, formatPct, formatNum, formatTime, formatDuration, pnlClass, cloneDraft, poolTypeOf,
} from './fleetFormat'

const REFRESH_MS = 15000

export default {
  name: 'FleetDetail',
  components: {
    BCard, BRow, BCol, BBadge, BButton, BAlert, BSpinner, BTabs, BTab, BTable, BFormCheckbox, BFormRadioGroup, VueApexCharts, RiskForm, EngineSlotsEditor, CheckPanel,
  },
  data() {
    return {
      notFound: false,
      tab: 0,
      now: Date.now(),
      timer: null,
      posStatus: 'OPEN',
      decFilter: {},
      decLimit: 100,
      edit: null,
      editBase: '',
      editCheck: { errors: [], warnings: [], tested: true },
      editExpected: null,
      previewTimer: null,
      apiKeys: [],
      engineFields: [
        { key: 'enabled', label: 'Açık' }, { key: 'label', label: 'Motor' }, { key: 'open', label: 'Dolu / max' },
        { key: 'trades', label: 'İşlem' }, { key: 'winRate', label: 'Kazanma' }, { key: 'net', label: 'Net K/Z' },
      ],
      positionFields: [
        { key: 'engineLabel', label: 'Motor' }, { key: 'symbol', label: 'Coin' }, { key: 'side', label: 'Yön' }, { key: 'entryPrice', label: 'Giriş' },
        { key: 'exit', label: 'Çıkış / stop' }, { key: 'pnl', label: 'K/Z' }, { key: 'openedAt', label: 'Açılış' }, { key: 'end', label: 'Kapanış / kalan' },
      ],
      decisionFields: [
        { key: 'decisionMs', label: 'Mum kapanışı' }, { key: 'engineLabel', label: 'Motor' }, { key: 'symbol', label: 'Coin' }, { key: 'side', label: 'Yön' },
        { key: 'outcome', label: 'Sonuç' }, { key: 'refPrice', label: 'Fiyat' }, { key: 'sizeMult', label: 'Boy çarpanı' },
      ],
      botFields: [
        { key: 'name', label: 'Bot' }, { key: 'status', label: 'Durum' }, { key: 'symbol', label: 'Coin' },
        { key: 'capitalCurrent', label: 'Bakiye' }, { key: 'realizedPnl', label: 'Gerçekleşen K/Z' },
      ],
    }
  },
  computed: {
    id() { return Number(this.$route.params.id) },
    d() { const v = this.$store.getters['fleet/detail']; return v && v.fleet && v.fleet.id === this.id ? v : null },
    f() { return this.d.fleet },
    k() { return this.d.kpis },
    busy() { return this.$store.getters['fleet/busy'] },
    positions() { return this.$store.getters['fleet/positions'] },
    decisions() { return this.$store.getters['fleet/decisions'] },
    saveStatus() { return this.$store.getters['fleet/dataSaveStatus'] },
    status() { return STATUS_INFO[this.f.status] || { label: this.f.status, variant: 'light-secondary' } },
    sizing() { return SIZING_INFO[this.f.sizingMode] || { label: this.f.sizingMode } },
    pool() { return POOL_INFO[poolTypeOf(this.d.draft.slotCount, this.d.draft.engines)] },
    canStart() { return ['DRAFT', 'PAUSED', 'STOPPED'].includes(this.f.status) },
    testedEngines() { const t = this.$store.getters['fleet/templates']; return t.length ? t[0].draft.engines : {} },
    tiles() {
      const k = this.k
      return [
        { label: 'Cüzdan', value: this.usd(k.wallet), sub: `bütçe ${this.usd(this.f.budget)}` },
        { label: 'Net K/Z', value: this.usd(k.netPnl, true), cls: pnlClass(k.netPnl), sub: `${k.trades} işlem · kazanma ${k.trades ? `${k.winRate.toFixed(1)}%` : '—'}` },
        { label: 'Bu ay', value: this.usd(k.thisMonth, true), cls: pnlClass(k.thisMonth) },
        { label: 'Maks DD (gerçekleşen)', value: k.maxDdPct ? this.pct(k.maxDdPct) : '0%', cls: k.maxDdPct < 0 ? 'text-danger' : '', sub: this.usd(k.maxDdUsd) },
        { label: 'Açık pozisyon', value: `${k.openPositions} / ${this.f.slotCount}`, sub: k.openPositions ? `anlık ${this.usd(k.unrealizedPnl, true)}` : '' },
        {
          label: 'BTC oynaklık çarpanı',
          value: k.volMultiplier == null ? '—' : num2(k.volMultiplier),
          sub: this.f.sizingMode === 'FIXED' ? 'son sinyalden' : `çeyrek sıfırlama ${k.nextQuarterAt ? new Date(k.nextQuarterAt).toLocaleDateString('tr-TR') : '—'}`,
        },
      ]
    },
    chartSeries() { return [{ name: 'Net K/Z', data: this.d.monthly.map(m => Number(m.net.toFixed(2))) }] },
    chartOptions() {
      const e = this.d.expected
      const lines = e ? [
        { y: e.weightedMonthly, borderColor: '#7367f0', label: { text: `test ağırlıklı ${this.usd(e.weightedMonthly)}`, style: { background: '#7367f0', color: '#fff' } } },
        { y: e.jan2026SepMonthly, borderColor: '#ff9f43', label: { text: `test Oca-Eyl 2026 ${this.usd(e.jan2026SepMonthly)}`, style: { background: '#ff9f43', color: '#fff' } } },
      ] : []
      return {
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '50%', colors: { ranges: [{ from: -1e12, to: 0, color: '#ea5455' }, { from: 0, to: 1e12, color: '#28c76f' }] } } },
        dataLabels: { enabled: false },
        xaxis: { categories: this.d.monthly.map(m => m.month) },
        yaxis: { labels: { formatter: v => Number(v).toFixed(0) } },
        annotations: { yaxis: lines },
        tooltip: { y: { formatter: (v, { dataPointIndex }) => `${this.usd(v, true)} · ${(this.d.monthly[dataPointIndex] || {}).trades || 0} işlem` } },
      }
    },
    decTotal() { return this.decisions.counts.reduce((s, c) => s + c.count, 0) },
    openedCount() { return this.decisions.counts.filter(c => c.outcome === 'OPENED').reduce((s, c) => s + c.count, 0) },
    skipCounts() { return this.decisions.counts.filter(c => c.outcome === 'SKIPPED').sort((a, b) => b.count - a.count) },
    locks() {
      const s = this.f.status
      const open = this.k.openPositions
      const l = {}
      if (s === 'RUNNING') {
        const why = 'filo çalışırken değiştirilemez (önce Duraklat)'
        l.slots = why; l.leverage = why; l.sizingMode = why
      }
      if (s !== 'DRAFT') { l.budget = 'yalnız Taslak durumunda değişir'; l.slotCount = 'yalnız Taslak durumunda değişir' }
      if (!((s === 'DRAFT' || s === 'STOPPED') && open === 0)) l.mode = 'yalnız Taslak / Durduruldu durumunda, açık pozisyon yokken değişir'
      return l
    },
    dirty() { return !!this.edit && JSON.stringify(this.edit) !== this.editBase },
    hasErrors() { return !!(this.editCheck.errors && this.editCheck.errors.length) },
  },
  watch: {
    saveStatus(val) {
      if (val.status === true) this.toast('success', 'Tamam', val.message)
      else if (val.status === false) this.toast('danger', 'Hata', val.message)
    },
    d(v, old) {
      // the settings form follows the server unless the user is editing it
      if (v && (!this.edit || !this.dirty || !old || old.fleet.id !== v.fleet.id)) this.resetEdit()
    },
    edit: { deep: true, handler() { if (this.dirty) this.schedulePreview() } },
  },
  created() {
    this.$store.commit('fleet/SET_DETAIL', null)
    this.$store.dispatch('fleet/getTemplates')
    this.load().then(() => this.loadTab())
    this.timer = setInterval(() => { this.now = Date.now(); this.load(); this.loadTab() }, REFRESH_MS)
    axiosIns.post('/api-keys', { limit: 100 }).then(r => { this.apiKeys = Array.isArray(r.data.data) ? r.data.data : [] }).catch(() => { this.apiKeys = [] })
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
    if (this.previewTimer) clearTimeout(this.previewTimer)
  },
  methods: {
    load() {
      return this.$store.dispatch('fleet/getDetail', this.id).then(() => { this.notFound = !this.d })
    },
    onTab(i) { this.tab = i; this.loadTab() },
    loadTab() {
      if (this.tab === 1) this.loadPositions()
      if (this.tab === 2) this.loadDecisions()
    },
    loadPositions() {
      return this.$store.dispatch('fleet/getPositions', { id: this.id, params: { status: this.posStatus || undefined, limit: 200 } })
    },
    loadDecisions() {
      return this.$store.dispatch('fleet/getDecisions', { id: this.id, params: { ...this.decFilter, limit: this.decLimit } })
    },
    setDecFilter(fl) { this.decFilter = fl; this.decLimit = 100; this.loadDecisions() },
    resetEdit() {
      if (!this.d) return
      this.edit = cloneDraft(this.d.draft)
      this.editBase = JSON.stringify(this.edit)
      this.editCheck = this.d.check
      this.editExpected = this.d.expected
    },
    resetEngines() { this.edit = { ...this.edit, engines: cloneDraft(this.testedEngines) } },
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(() => {
        this.$store.dispatch('fleet/preview', this.edit)
          .then(r => { this.editCheck = r.check; this.editExpected = r.expected })
          .catch(() => { this.editCheck = { errors: ['Ayar kontrol edilemedi (eksik veya geçersiz alan).'], warnings: [], tested: false } })
      }, 300)
    },
    onSave() {
      this.$store.dispatch('fleet/update', { id: this.id, patch: this.edit }).then(() => this.resetEdit()).catch(() => {})
    },
    toggleEngine(engine, enabled) {
      const cur = this.d.draft.engines[engine] || {}
      this.$store.dispatch('fleet/update', { id: this.id, patch: { engines: { [engine]: { ...cur, enabled } } } }).catch(() => this.load())
    },
    setStatus(action) {
      const ask = {
        start: this.f.mode === 'LIVE' ? 'Filo LIVE modda: GERÇEK emir gönderecek. Başlatılsın mı?' : 'Filo başlatılsın mı? İlk işlem bir sonraki mum kapanışında yapılır.',
        pause: 'Yeni pozisyon açılmayacak, açık pozisyonlar kendi çıkışlarıyla kapanacak. Duraklatılsın mı?',
        stop: 'Filo durdurulsun mu? (Açık pozisyon varsa önce Duraklat veya Acil Kapat gerekir.)',
      }[action]
      this.$bvModal.msgBoxConfirm(ask, {
        title: 'Onay', okTitle: 'Evet', cancelTitle: 'Vazgeç', centered: true, okVariant: action === 'start' && this.f.mode === 'LIVE' ? 'danger' : 'primary',
      }).then(ok => { if (ok) this.$store.dispatch('fleet/setStatus', { id: this.id, action }).catch(() => {}) })
    },
    onKill() {
      this.$bvModal.msgBoxConfirm(`${this.k.openPositions} açık pozisyonun TAMAMI piyasa fiyatından hemen kapatılacak ve filo durdurulacak. Bu işlem geri alınamaz.`, {
        title: 'ACİL KAPAT', okTitle: 'Evet, hepsini kapat', cancelTitle: 'Vazgeç', okVariant: 'danger', centered: true,
      }).then(ok => { if (ok) this.$store.dispatch('fleet/setStatus', { id: this.id, action: 'kill' }).catch(() => {}) })
    },
    onDelete() {
      this.$bvModal.msgBoxConfirm('Filo ve slot botları silinecek (geçmiş pozisyon kayıtları kalır). Emin misin?', {
        title: 'Filoyu sil', okTitle: 'Sil', cancelTitle: 'Vazgeç', okVariant: 'danger', centered: true,
      }).then(ok => {
        if (ok) this.$store.dispatch('fleet/remove', this.id).then(() => this.$router.push({ name: 'Fleet' })).catch(() => {})
      })
    },
    engineDesc(e) { return (ENGINE_INFO[e] || {}).desc },
    reasonLabel(r) { return REASON_INFO[r] || r || '—' },
    closeReason(r) { return CLOSE_REASON_INFO[r] || r || '—' },
    usd: (v, sign) => formatUsd(v, sign),
    pct: v => formatPct(v),
    num: (v, dg) => formatNum(v, dg),
    time: v => formatTime(v),
    duration: ms => formatDuration(ms),
    pnlClass: v => pnlClass(v),
    toast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, icon: variant === 'success' ? 'CheckIcon' : 'XIcon', text, variant,
        },
      })
    },
  },
}

function num2(v) { return Number(v).toFixed(2) }
</script>
